import React from 'react';
import { TextField, Select, Button, Flex, Text, type SelectOption } from '@chatsdet/ui';
import type { ApiSequenceExport } from '@chatsdet/types';
import { Plus, Trash } from '@phosphor-icons/react';

type ApiSequenceExportsEditorProps = {
  exports: ApiSequenceExport[];
  onChange: (exports: ApiSequenceExport[]) => void;
  availableStepIds: string[];
};

export function ApiSequenceExportsEditor({
  exports: exportsList,
  onChange,
  availableStepIds,
}: ApiSequenceExportsEditorProps) {
  const stepOptions: SelectOption[] = React.useMemo(
    () => [
      { id: '', label: 'Select step...' },
      ...availableStepIds.map((id) => ({ id, label: id })),
    ],
    [availableStepIds]
  );

  const sourceOptions: SelectOption[] = [
    { id: 'response', label: 'Response body' },
    { id: 'request', label: 'Request body' },
  ];

  const addExport = () => {
    onChange([
      ...exportsList,
      { variableName: '', stepId: '', responsePath: '' },
    ]);
  };

  const updateExport = (index: number, patch: Partial<ApiSequenceExport>) => {
    const updated = [...exportsList];
    updated[index] = { ...updated[index], ...patch };
    onChange(updated);
  };

  const removeExport = (index: number) => {
    onChange(exportsList.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-3">
      {exportsList.map((exp, index) => (
        <div
          key={index}
          className="rounded-lg border border-border bg-card p-4 space-y-3"
        >
          <Flex align="center" justify="between">
            <Text size="xs" weight="medium" className="text-muted-foreground">
              Export {index + 1}
            </Text>
            <Button
              variant="icon"
              size="sm"
              iconOnly
              tooltip="Remove export"
              onPress={() => removeExport(index)}
              className="text-muted-foreground hover:text-destructive"
            >
              <Trash size={14} />
            </Button>
          </Flex>

          <div className="grid grid-cols-2 gap-3">
            <TextField
              label="Variable Name"
              aria-label="Variable name"
              value={exp.variableName}
              onChange={(value) => updateExport(index, { variableName: value })}
              placeholder="project_id"
              className="font-mono text-sm"
            />
            <Select
              label="Step"
              aria-label="Source step"
              items={stepOptions}
              selectedKey={exp.stepId}
              onSelectionChange={(key) =>
                updateExport(index, { stepId: String(key) })
              }
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Select
              label="Source"
              aria-label="Extract from"
              items={sourceOptions}
              selectedKey={exp.source ?? 'response'}
              onSelectionChange={(key) =>
                updateExport(index, { source: key === 'request' ? 'request' : undefined })
              }
            />
            <TextField
              label="Path"
              aria-label="Path in body"
              value={exp.responsePath}
              onChange={(value) => updateExport(index, { responsePath: value })}
              placeholder="data.id"
              className="font-mono text-sm"
              description={`Dot-notation path in the JSON ${exp.source === 'request' ? 'request' : 'response'} body`}
            />
          </div>

          <TextField
            label="Description"
            aria-label="Export description"
            value={exp.description || ''}
            onChange={(value) =>
              updateExport(index, { description: value || undefined })
            }
            placeholder="ID of the created project"
          />
        </div>
      ))}

      <Button
        variant="outline"
        size="sm"
        onPress={addExport}
        iconLeft={<Plus size={14} />}
      >
        Add Export
      </Button>
    </div>
  );
}
